import React from 'react'
import { compose } from 'recompose'
import { connect, ConnectedProps } from 'react-redux'
import { BButton, BTitle, BRow, BCol, BIcon, BLink } from '@prague-digi/ui-components'
import classNames from '@prague-digi/class-names'
import truncate from 'lodash/truncate'

import messages from '../../i18n'
import { ReduxState } from '../../types'
import { intl, errorHandler, IntlInterface, wizard, WizardInterface } from '../../hoc'
import { modalDialogActions } from '../../redux'
import { translateCodetable, translateOccupation } from '../../utils/mix'
import { formatAddress } from '../../utils/helpers'
import { SecondDocumentType, PepOptions } from '../../constants/enums'
import ClientAddress from '../ClientAddress/ClientAddress'
import LoanPreConfigurator from '../../components/LoanPreConfigurator/LoanPreConfigurator'
import ClientInfo from '../ClientInfo/ClientInfo'
import ClientDetails from '../ClientDetails/ClientDetails'
import FinanceProfession from '../FinanceProfession/FinanceProfession'
import FinanceEmployer from '../FinanceEmployer/FinanceEmployer'
import FinanceIncome from '../FinanceIncome/FinanceIncome'
import FinanceExpenses from '../FinanceExpenses/FinanceExpenses'
import ClientAdditionalInfo from '../ClientAdditionalInfo/ClientAdditionalInfo'
import styles from './styles.scss'
import { Disclaimer, Footer, ModalWizard } from './components'

interface Props extends IntlInterface, WizardInterface, ConnectedProps<typeof connector> {
  handleSubmit: any
  setErrors: any
  values: any
  errors: any
  initialize: any
}

interface State {
  wizardData?: any[]
}

const noUnderlineRowClass = classNames(styles.row, styles.noUnderline)

export class Recapitulation extends React.Component<Props, State> {
  state: State = {
    wizardData: undefined,
  }

  translateOccupation = (employmentTypeCode: any, occupationCode: any) =>
    translateOccupation(
      this.props.codetableStore.occupationsByEmploymentType,
      employmentTypeCode,
      occupationCode
    )

  gotoNext = () => {
    this.props.wizard.gotoNext()
  }

  showAddressModal = () => {
    this.setState({
      wizardData: [<ClientAddress key="ClientAddress" />],
    })
  }

  showChangeLoanModal = () => {
    this.setState({
      wizardData: [<LoanPreConfigurator key="LoanPreConfigurator" />],
    })
  }

  showClientInfoModal = () => {
    this.setState({
      wizardData: [<ClientInfo key="ClientInfo" />],
    })
  }

  showClientDetailsModal = () => {
    this.setState({
      wizardData: [<ClientDetails key="ClientDetails" />],
    })
  }

  showEmploymentModal = () => {
    this.setState({
      wizardData: [
        <FinanceProfession key="FinanceProfession" />,
        <FinanceEmployer key="FinanceEmployer" />,
      ],
    })
  }

  showFinanceIncomeModal = () => {
    this.setState({
      wizardData: [<FinanceIncome key="FinanceIncome" />],
    })
  }

  showFinanceExpensesModal = () => {
    this.setState({
      wizardData: [<FinanceExpenses key="FinanceExpenses" />],
    })
  }

  showClientAdditionalInfoModal = () => {
    this.setState({
      wizardData: [<ClientAdditionalInfo key="ClientAdditionalInfo" />],
    })
  }

  closeWizard = () => {
    this.setState({
      wizardData: undefined,
    })
  }

  render() {
    const {
      client,
      codetableStore,
      formatCzechCurrency,
      formatCzechDate,
      formatLoanDuration,
      formatPhoneNumber,
      product,
      translate,
    } = this.props

    const additionalDocument =
      client.otherDocumentType === SecondDocumentType.PASSPORT
        ? translate(messages.ClientAdditionalInfo.passport)
        : translate(messages.ClientAdditionalInfo.drivingLicense)

    const pep =
      client.pep === PepOptions.No
        ? translate(messages.common.no)
        : truncate(client.pepReason, { length: 16, separator: ' ' })

    return (
      <div>
        <BTitle type="h3" center>
          {translate(messages.recapitulation.title)}
        </BTitle>
        <div className={styles.loanSection}>
          <BTitle type="h4">{translate(messages.recapitulation.yourLoan)}</BTitle>
          <div className={styles.row}>
            <div>{translate(messages.recapitulation.loanAmount)}</div>
            <div>{formatCzechCurrency(product?.configuration.loanAmount)}</div>
          </div>
          <div className={styles.row}>
            <div>{translate(messages.recapitulation.loanDuration)}</div>
            <div>{formatLoanDuration(product?.configuration.term)}</div>
          </div>
          <div className={styles.row}>
            <div>{translate(messages.recapitulation.instalmentDay)}</div>
            <div>{`${product.instalmentDay}.`}</div>
          </div>
          <div className={styles.changeButtonWrapper}>
            <BButton
              messages={{
                message: translate(messages.recapitulation.changeLoanBtn),
              }}
              onClick={this.showChangeLoanModal}
              theme={styles.changeButton}
            />
          </div>
        </div>

        <div className={styles.section}>
          <div className={noUnderlineRowClass}>
            <div>
              <BTitle type="h4" theme={styles.noPadding}>
                {translate(messages.recapitulation.contactData)}
              </BTitle>
            </div>
            <div>
              <BLink
                type="link"
                testid="changeContactDataBtn"
                className={styles.changeBtn}
                onClick={this.showClientInfoModal}>
                <span className={styles.icon}>
                  <BIcon name="edit" size="sm" />
                </span>
                <span className={styles.text}>
                  {translate(messages.recapitulation.changeContactData)}
                </span>
              </BLink>
            </div>
          </div>
          <div className={styles.row}>
            <div>{translate(messages.recapitulation.firstNameAndLastName)}</div>
            <div>
              {client.firstName} {client.lastName}
            </div>
          </div>
          <BRow>
            <BCol xs="12" lg="6">
              <div className={styles.row}>
                <div>{translate(messages.recapitulation.phoneContact)}</div>
                <div>{formatPhoneNumber(client.phoneNumber)}</div>
              </div>
            </BCol>
            <BCol xs="12" lg="6">
              <div className={styles.row}>
                <div>{translate(messages.recapitulation.email)}</div>
                <div>{client.email}</div>
              </div>
            </BCol>
          </BRow>
        </div>

        <div className={styles.section}>
          <div className={noUnderlineRowClass}>
            <div>
              <BTitle type="h4" theme={styles.noPadding}>
                {translate(messages.recapitulation.address)}
              </BTitle>
            </div>
            <div>
              <BLink
                type="link"
                testid="changeAddressBtn"
                className={styles.changeBtn}
                onClick={this.showAddressModal}>
                <span className={styles.icon}>
                  <BIcon name="edit" size="sm" />
                </span>
                <span className={styles.text}>
                  {translate(messages.recapitulation.changeAddress)}
                </span>
              </BLink>
            </div>
          </div>
          <div className={styles.row}>
            <div>{translate(messages.clientAddress.permanentAddress)}</div>
            <div>{formatAddress(client.permanentAddress)}</div>
          </div>
          {client.differentContactAddress && (
            <div className={styles.row}>
              <div>{translate(messages.clientAddress.contactAddress.title)}</div>
              <div>{formatAddress(client.contactAddress)}</div>
            </div>
          )}
        </div>

        <div className={styles.section}>
          <div className={noUnderlineRowClass}>
            <div>
              <BTitle type="h4" theme={styles.noPadding}>
                {translate(messages.recapitulation.personalData)}
              </BTitle>
            </div>
            <div>
              <BLink
                type="link"
                testid="changePersonalDataBtn"
                className={styles.changeBtn}
                onClick={this.showClientDetailsModal}>
                <span className={styles.icon}>
                  <BIcon name="edit" size="sm" />
                </span>
                <span className={styles.text}>
                  {translate(messages.recapitulation.changePersonalData)}
                </span>
              </BLink>
            </div>
          </div>
          <BRow>
            <BCol xs="12" lg="6">
              <div className={styles.row}>
                <div>{translate(messages.clientDetails.idCardNumber)}</div>
                <div>{client.identityCardNumber}</div>
              </div>
              <div className={styles.row}>
                <div>{translate(messages.clientDetails.personalNumber)}</div>
                <div>{client.personalIdentificationNumber}</div>
              </div>
              <div className={styles.row}>
                <div>{translate(messages.clientDetails.birthPlace)}</div>
                <div>{client.placeOfBirth}</div>
              </div>
              <div className={styles.row}>
                <div>{translate(messages.clientDetails.nationality)}</div>
                <div>{translateCodetable(codetableStore.countries, client.citizenship)}</div>
              </div>
            </BCol>
            <BCol xs="12" lg="6">
              <div className={styles.row}>
                <div>{translate(messages.clientDetails.idCardValidity)}</div>
                <div>{formatCzechDate(client.identityCardExpiration)}</div>
              </div>
              <div className={styles.row}>
                <div>{translate(messages.clientDetails.maritalStatus)}</div>
                <div>
                  {translateCodetable(codetableStore.maritalStatuses, client.maritalStatus)}
                </div>
              </div>
              <div className={styles.row}>
                <div>{translate(messages.clientDetails.birthCountry)}</div>
                <div>{translateCodetable(codetableStore.countries, client.countryOfBirth)}</div>
              </div>
            </BCol>
          </BRow>
        </div>

        <div className={styles.section}>
          <div className={noUnderlineRowClass}>
            <div>
              <BTitle type="h4" theme={styles.noPadding}>
                {translate(messages.recapitulation.employment)}
              </BTitle>
            </div>
            <div>
              <BLink
                type="link"
                testid="changeEmploymentBtn"
                className={styles.changeBtn}
                onClick={this.showEmploymentModal}>
                <span className={styles.icon}>
                  <BIcon name="edit" size="sm" />
                </span>
                <span className={styles.text}>
                  {translate(messages.recapitulation.changeEmployment)}
                </span>
              </BLink>
            </div>
          </div>
          <BRow>
            <BCol xs="12" lg="6">
              <div className={styles.row}>
                <div>{translate(messages.FinanceProfession.employmentType)}</div>
                <div>
                  {translateCodetable(codetableStore.employmentTypes, client.employmentTypeCode)}
                </div>
              </div>
              <div className={styles.row}>
                <div>{translate(messages.recapitulation.employerName)}</div>
                <div>{client.employerName}</div>
              </div>
            </BCol>
            <BCol xs="12" lg="6">
              <div className={styles.row}>
                <div>{translate(messages.FinanceProfession.occupationType)}</div>
                <div>
                  {this.translateOccupation(client.employmentTypeCode, client.professionCode)}
                </div>
              </div>
              <div className={styles.row}>
                <div>{translate(messages.recapitulation.identificationNumber)}</div>
                <div>{client.employerId}</div>
              </div>
            </BCol>
          </BRow>
          <div className={styles.row}>
            <div>{translate(messages.recapitulation.employerAddress)}</div>
            <div>{formatAddress(client.employerAddress)}</div>
          </div>
          <BRow>
            <BCol xs="12" lg="6">
              <div className={styles.row}>
                <div>{translate(messages.financeEmployer.employedFrom)}</div>
                <div>{formatCzechDate(client.employedSince)}</div>
              </div>
            </BCol>
            {client.employedTo && (
              <BCol xs="12" lg="6">
                <div className={styles.row}>
                  <div>{translate(messages.financeEmployer.employedTo)}</div>
                  <div>{formatCzechDate(client.employedTo)}</div>
                </div>
              </BCol>
            )}
          </BRow>
        </div>

        <div className={styles.section}>
          <div className={noUnderlineRowClass}>
            <div>
              <BTitle type="h4" theme={styles.noPadding}>
                {translate(messages.recapitulation.incomes)}
              </BTitle>
            </div>
            <div>
              <BLink
                type="link"
                testid="changeIncomesBtn"
                className={styles.changeBtn}
                onClick={this.showFinanceIncomeModal}>
                <span className={styles.icon}>
                  <BIcon name="edit" size="sm" />
                </span>
                <span className={styles.text}>
                  {translate(messages.recapitulation.changeIncomes)}
                </span>
              </BLink>
            </div>
          </div>
          <div className={styles.row}>
            <div>{translate(messages.FinanceIncome.netIncomeLabel)}</div>
            <div>{formatCzechCurrency(client.net3MonthIncome)}</div>
          </div>
          <div className={styles.row}>
            <div>{translate(messages.FinanceIncome.familyIncomeLabel)}</div>
            <div>{formatCzechCurrency(client.net3MonthHouseholdIncome)}</div>
          </div>
          <BRow>
            <BCol xs="12" lg="6">
              {client.taxCrOnlyFlag && (
                <div className={styles.row}>{translate(messages.FinanceIncome.taxesInCzechia)}</div>
              )}
            </BCol>
          </BRow>
        </div>

        <div className={styles.section}>
          <div className={noUnderlineRowClass}>
            <div>
              <BTitle type="h4" theme={styles.noPadding}>
                {translate(messages.recapitulation.expenses)}
              </BTitle>
            </div>
            <div>
              <BLink
                type="link"
                testid="changeOutcomesBtn"
                className={styles.changeBtn}
                onClick={this.showFinanceExpensesModal}>
                <span className={styles.icon}>
                  <BIcon name="edit" size="sm" />
                </span>
                <span className={styles.text}>
                  {translate(messages.recapitulation.changeExpenses)}
                </span>
              </BLink>
            </div>
          </div>
          <div className={styles.row}>
            <div>{translate(messages.financeExpenses.installmentsOutsideMoneta)}</div>
            <div>{formatCzechCurrency(client.otherMonthlyInstallments)}</div>
          </div>
          <div className={styles.row}>
            <div>{translate(messages.financeExpenses.housingExpenditure)}</div>
            <div>{formatCzechCurrency(client.housingExpenditure)}</div>
          </div>
          <div className={styles.row}>
            <div>{translate(messages.financeExpenses.otherCostOfLiving)}</div>
            <div>{formatCzechCurrency(client.otherCostOfLiving)}</div>
          </div>
          <div className={styles.row}>
            <div>{translate(messages.financeExpenses.numberOfDependents)}</div>
            <div>
              {translate(messages.financeExpenses.mePlus)} {client.numberOfDependents}
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={noUnderlineRowClass}>
            <div>
              <BTitle type="h4" theme={styles.noPadding}>
                {translate(messages.recapitulation.additionalInfo)}
              </BTitle>
            </div>
            <div>
              <BLink
                type="link"
                testid="changeAdditionalInfoBtn"
                className={styles.changeBtn}
                onClick={this.showClientAdditionalInfoModal}>
                <span className={styles.icon}>
                  <BIcon name="edit" size="sm" />
                </span>
                <span className={styles.text}>
                  {translate(messages.recapitulation.changeAdditionalInfo)}
                </span>
              </BLink>
            </div>
          </div>
          <div className={styles.row}>
            <div>{translate(messages.ClientAdditionalInfo.education)}</div>
            <div>{translateCodetable(codetableStore.education, client.education)}</div>
          </div>
          <BRow>
            <BCol xs="12" lg="6">
              <div className={styles.row}>
                <div>{translate(messages.ClientAdditionalInfo.stayOnAddressFrom)}</div>
                <div>
                  {translateCodetable(codetableStore.stayDurations, client.stayOnAddressDuration)}
                </div>
              </div>
            </BCol>
            <BCol xs="12" lg="6">
              <div className={styles.row}>
                <div>{translate(messages.ClientAdditionalInfo.livingType)}</div>
                <div>{translateCodetable(codetableStore.housingTypes, client.livingType)}</div>
              </div>
            </BCol>
          </BRow>
          <BRow>
            <BCol xs="12" lg="6">
              <div className={styles.row}>
                <div>{translate(messages.recapitulation.additionalDocuments)}</div>
                <div>{additionalDocument}</div>
              </div>
            </BCol>
            <BCol xs="12" lg="6">
              <div className={styles.row}>
                <div>{translate(messages.recapitulation.pep)}</div>
                <div>{pep}</div>
              </div>
            </BCol>
          </BRow>
        </div>

        <Disclaimer />

        {/* CLOANS-2370 <TelcoScoringConsent /> */}

        <Footer
          showClientInfoModal={this.showClientInfoModal}
          className={styles.section}
          gotoNext={this.gotoNext}
        />

        {this.state.wizardData && (
          <ModalWizard onClose={this.closeWizard}>{this.state.wizardData}</ModalWizard>
        )}
      </div>
    )
  }
}

const connector = connect(
  (state: ReduxState) => ({
    client: state.client,
    product: state.product,
    codetableStore: state.codetable,
  }),
  {
    showModalDialog: modalDialogActions.showModalDialog,
    hideModalDialog: modalDialogActions.hideModalDialog,
  }
)

export default compose(
  errorHandler('containers/Recapitulation/Recapitulation.tsx'),
  wizard,
  connector,
  intl
)(Recapitulation)
